/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useState } from 'react';
import { Button, FeedbackIdeaIcon, FormProvider, FormSubmitWithRecaptcha, Popup, Text, TextAreaField } from '@/shared/ui';
import { notifications } from '@/shared/ui/components/basic/Notifications';
import { useAuth } from '@context/authentication';
import { useTranslation } from 'react-i18next';
import { submitSuggestionFeedback } from '@/shared/services/submit-suggestion-feedback';

interface HeaderFeedbackPopupProps {
  visible: boolean;
  setVisible: (visible: boolean) => void;
}

/**
 * Feedback popup rendered by the GlobalHeader.
 *
 * Unlike the AppLayout-scoped FeedbackPopup, this variant has NO dependency on
 * the AppLayout-scoped Zustand stores (useLayout / useGlobalError) or on the
 * router context, because the GlobalHeader is mounted ABOVE AppLayout in the
 * component tree.
 */
const HeaderFeedbackPopup: React.FC<HeaderFeedbackPopupProps> = ({ visible, setVisible }) => {
  const auth = useAuth();
  const { t } = useTranslation('features/popups');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!visible) return <></>;

  async function handleSubmitFeedback(formValues: { message?: string }): Promise<void> {
    if (isSubmitting) return;
    try {
      if (!auth.user?.access_token) {
        notifications.show({ color: 'red', message: t('feedback.errors.notAuthenticated') });
        return;
      }
      setIsSubmitting(true);
      await submitSuggestionFeedback(formValues.message || '');
      notifications.show({ color: 'green', message: t('feedback.success') });
      setVisible(false);
    } catch {
      notifications.show({ color: 'red', message: t('feedback.errors.sendError') });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Popup onClose={() => setVisible(false)} icon={FeedbackIdeaIcon} title={t('feedback.title')}>
      <FormProvider required={['message']}>
        <div className="flex w-full flex-col gap-(--space-4)">
          <Text className="m-0 text-(length:--text-sm) leading-relaxed text-(--text-secondary)">
            {t('feedback.intro')}
          </Text>
          <div className="w-full">
            <TextAreaField
              name="message"
              type="text"
              rows={6}
              autoFocus
              className="w-full"
            />
          </div>
          <div className="flex w-full flex-row items-center justify-end gap-(--space-3)">
            <Button variant="outlined" size="sm" disabled={isSubmitting} onClick={() => setVisible(false)}>
              {t('feedback.cancel')}
            </Button>
            <FormSubmitWithRecaptcha
              recaptchaAction="feedback_submission"
              onSubmit={(state) =>
                handleSubmitFeedback(state.formValues as { message?: string })
              }
            >
              <Button size="lg" className="min-w-[140px]" loading={isSubmitting}>
                {t('feedback.submit')}
              </Button>
            </FormSubmitWithRecaptcha>
          </div>
        </div>
      </FormProvider>
    </Popup>
  );
};

export default HeaderFeedbackPopup;
